import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { Account, Beneficiary } from '@shared/contracts/entities';
import type { MobilePaymentDraft } from '@/app/contracts/mobileTypes';
import {
  getAccounts,
  getBeneficiaries,
  submitPayment,
  validatePayment,
  verifySca,
  requestStepUp,
  type PaymentDraftInput,
  type PaymentValidation,
  type SubmitPaymentResult,
} from '@/mocks/api';
import { useAsync, useMutation } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { newIdempotencyKey } from '@/services/idempotency';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import {
  Amount,
  DetailList,
  Initials,
  ListRow,
  ResultMedallion,
  SectionHeader,
  StatusBadge,
} from '@/components/banking';
import { ErrorState, LoadingRegion, Notice, RowSkeleton, EmptyState } from '@/components/feedback';
import { formatAmount, formatDateTime, formatIban, formatSortCode } from '@/services/formatters';
import { isMockApiError } from '@/mocks/errors';
import { nowIso } from '@/services/clock';
import { TransferFlow } from './TransferFlow';

/**
 * Send money — WF-SMEM-06.
 *
 * A full-page step flow, not a single long form: the package requires
 * "full-page steps for financial forms" and "a review step before any money
 * movement". Entered values survive going back.
 *
 * The step names follow `MobilePaymentDraft['currentStep']` in the shared
 * contract, so this flow and the contract cannot drift apart.
 */

type Step = MobilePaymentDraft['currentStep'];

const STEP_ORDER: readonly Step[] = [
  'source',
  'beneficiary',
  'verification',
  'details',
  'review',
  'sca',
  'result',
];

const STEP_TITLE: Record<Step, string> = {
  source: 'Pay from',
  beneficiary: 'Pay to',
  verification: 'Check the payee',
  details: 'Amount and reference',
  review: 'Review payment',
  sca: 'Confirm it is you',
  result: 'Payment result',
};

export function NewPaymentPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const resolved = resolveContext();
  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  // `?mode=transfer` is a genuinely different workflow (own account to own
  // account, no payee and no SCA), so it renders its own flow rather than
  // pretending to be a payment.
  const isTransfer = params.get('mode') === 'transfer';

  const [step, setStep] = useState<Step>('source');
  const [sourceAccountId, setSourceAccountId] = useState('');
  const [beneficiaryId, setBeneficiaryId] = useState(params.get('beneficiaryId') ?? '');
  const [amountText, setAmountText] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [acknowledgedWarning, setAcknowledgedWarning] = useState(false);

  const [validation, setValidation] = useState<PaymentValidation | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [scaCode, setScaCode] = useState('');
  const [scaError, setScaError] = useState<string | null>(null);
  const [result, setResult] = useState<SubmitPaymentResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * One idempotency key per intent. It is created when the user reaches review
   * and reused for every submit attempt of that same payment, so a double tap
   * replays the first result instead of sending twice. A deliberate "try again"
   * after a failure mints a new key, which the adapter records as a new attempt.
   */
  const idempotencyKey = useRef<string>(newIdempotencyKey('pay'));

  const amountRef = useRef<HTMLInputElement | null>(null);
  const referenceRef = useRef<HTMLInputElement | null>(null);

  const accounts = useAsync(async () => {
    if (!companyId || !userId) return [];
    return (await getAccounts(companyId, userId)).data;
  }, [companyId, userId]);

  const beneficiaries = useAsync(async () => {
    if (!companyId) return [];
    return (await getBeneficiaries(companyId)).data;
  }, [companyId]);

  const submitMutation = useMutation(async (input: PaymentDraftInput) => {
    if (!companyId || !userId) throw new Error('no session');
    return (await submitPayment(companyId, userId, input, idempotencyKey.current)).data;
  });

  const sourceAccount: Account | undefined = (accounts.data ?? []).find(
    (a) => a.id === sourceAccountId,
  );
  const beneficiary: Beneficiary | undefined = (beneficiaries.data ?? []).find(
    (b) => b.id === beneficiaryId,
  );

  const amountMinor = useMemo(() => {
    const n = Number(amountText.replace(/,/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : NaN;
  }, [amountText]);

  const draft: PaymentDraftInput | null =
    sourceAccount && beneficiary
      ? {
          sourceAccountId,
          beneficiaryId,
          amount: Number.isFinite(amountMinor) ? amountMinor : 0,
          currency: sourceAccount.currency,
          reference: reference.trim(),
          requestedExecutionDate: nowIso(),
          ...(note.trim() ? { noteToBeneficiary: note.trim() } : {}),
        }
      : null;

  // Default to the first account that can actually send.
  useEffect(() => {
    if (sourceAccountId || accounts.status !== 'success') return;
    const sendable = accounts.data.find((a) => a.status === 'active');
    if (sendable) setSourceAccountId(sendable.id);
  }, [accounts.status, accounts.data, sourceAccountId]);

  if (!resolved) return null;
  if (isTransfer) return <TransferFlow />;

  const stepIndex = STEP_ORDER.indexOf(step);

  const goBack = () => {
    if (step === 'result') {
      navigate('/mobile/payments');
      return;
    }
    // Skip the verification step going back when there was nothing to warn about.
    const previous = STEP_ORDER[Math.max(0, stepIndex - 1)];
    if (previous === 'verification' && beneficiary?.verificationResult === 'match') {
      setStep('beneficiary');
      return;
    }
    if (previous) setStep(previous);
  };

  const runValidation = async (): Promise<PaymentValidation | null> => {
    if (!companyId || !userId || !draft) return null;
    const response = await validatePayment(companyId, userId, draft);
    setValidation(response.data);
    setFieldErrors({ ...response.data.fieldErrors });
    return response.data;
  };

  const continueFromDetails = async () => {
    const v = await runValidation();
    if (!v) return;
    if (!v.valid) {
      if (v.fieldErrors['amount']) amountRef.current?.focus();
      else if (v.fieldErrors['reference']) referenceRef.current?.focus();
      return;
    }
    setStep('review');
  };

  const startSca = async () => {
    if (!userId) return;
    setScaError(null);
    setScaCode('');
    const outcome = await requestStepUp(userId, `payment:${idempotencyKey.current}`);
    setChallengeId(outcome.data.challengeId);
    setStep('sca');
  };

  const confirmAndSend = async () => {
    if (!challengeId || !draft) return;
    setScaError(null);
    try {
      await verifySca(challengeId, scaCode);
    } catch (e: unknown) {
      setScaError(isMockApiError(e) ? e.message : 'That code was not correct.');
      setScaCode('');
      return;
    }
    setSubmitError(null);
    const outcome = await submitMutation.run(draft);
    if (outcome.ok) {
      setResult(outcome.value);
      setStep('result');
    } else if (outcome.error) {
      if (isMockApiError(outcome.error) && outcome.error.fieldErrors) {
        setFieldErrors(outcome.error.fieldErrors);
        setStep('details');
      } else {
        setSubmitError(isMockApiError(outcome.error) ? outcome.error.message : 'The payment could not be sent.');
        setStep('review');
      }
    }
  };

  const warningsBlockingContinue =
    (validation?.businessWarnings.length ?? 0) > 0 && !acknowledgedWarning;

  return (
    <>
      <MobileHeader
        title={STEP_TITLE[step]}
        {...(step === 'result'
          ? { backTo: '/mobile/payments', backLabel: 'Payments' }
          : { backLabel: stepIndex > 0 ? STEP_TITLE[STEP_ORDER[stepIndex - 1]!] : 'Payments' })}
      />

      {step !== 'result' ? (
        <p className="tm-hint" role="status" style={{ marginBlockEnd: 8 }}>
          Step {stepIndex + 1} of {STEP_ORDER.length - 1}
        </p>
      ) : null}

      {/* ------------------------------------------------------- source -- */}
      {step === 'source' ? (
        <div className="tm-stack">
          {accounts.status === 'loading' ? (
            <LoadingRegion label="Loading accounts">
              <div className="tm-card tm-card--flush">
                <RowSkeleton count={3} />
              </div>
            </LoadingRegion>
          ) : accounts.status === 'error' ? (
            <ErrorState error={accounts.error} onRetry={accounts.reload} />
          ) : (
            <>
              <div className="tm-card tm-card--flush">
                <ul className="tm-rows">
                  {accounts.data.map((account) => {
                    const sendable = account.status === 'active';
                    return (
                      <li key={account.id}>
                        <ListRow
                          {...(sendable
                            ? {
                                onClick: () => {
                                  setSourceAccountId(account.id);
                                  setStep('beneficiary');
                                },
                              }
                            : {})}
                          title={account.displayName}
                          meta={
                            sendable
                              ? `${account.currency} available`
                              : (account.statusReason ?? 'Cannot send from this account')
                          }
                          end={
                            <span style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                              <Amount
                                minorUnits={account.availableBalance}
                                currency={account.currency}
                              />
                              {!sendable ? <StatusBadge status={account.status} /> : null}
                            </span>
                          }
                          ariaLabel={`${account.displayName}, ${sendable ? 'available to send from' : 'cannot send from this account'}`}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
              <p className="tm-hint">
                Only accounts that can send money are selectable.
              </p>
            </>
          )}
        </div>
      ) : null}

      {/* -------------------------------------------------- beneficiary -- */}
      {step === 'beneficiary' ? (
        <div className="tm-stack">
          {beneficiaries.status === 'loading' ? (
            <LoadingRegion label="Loading beneficiaries">
              <div className="tm-card tm-card--flush">
                <RowSkeleton count={4} />
              </div>
            </LoadingRegion>
          ) : (beneficiaries.data ?? []).length === 0 ? (
            <EmptyState
              title="No beneficiaries yet"
              body="Add someone to pay before you can send money."
              action={
                <Link to="/mobile/beneficiaries" className="tm-btn">
                  Add a beneficiary
                </Link>
              }
            />
          ) : (
            <div className="tm-card tm-card--flush">
              <ul className="tm-rows">
                {(beneficiaries.data ?? [])
                  .filter((b) => b.status === 'active')
                  .filter((b) => !sourceAccount || b.currency === sourceAccount.currency)
                  .map((b) => (
                    <li key={b.id}>
                      <ListRow
                        onClick={() => {
                          setBeneficiaryId(b.id);
                          setAcknowledgedWarning(false);
                          setStep(b.verificationResult === 'match' ? 'details' : 'verification');
                        }}
                        leading={<Initials name={b.name} />}
                        title={b.name}
                        meta={
                          b.iban
                            ? formatIban(b.iban)
                            : `${formatSortCode(b.sortCode ?? '')} · ${b.accountNumber ?? ''}`
                        }
                        end={
                          b.verificationResult !== 'match' ? (
                            <StatusBadge status={b.verificationResult} />
                          ) : null
                        }
                        ariaLabel={`Pay ${b.name}, payee check ${b.verificationResult}`}
                      />
                    </li>
                  ))}
              </ul>
            </div>
          )}
          {sourceAccount ? (
            <p className="tm-hint">
              Showing beneficiaries that can receive {sourceAccount.currency}.
            </p>
          ) : null}
        </div>
      ) : null}

      {/* ------------------------------------------------- verification -- */}
      {step === 'verification' && beneficiary ? (
        <div className="tm-stack">
          <Notice
            tone={beneficiary.verificationResult === 'no-match' ? 'danger' : 'warning'}
            title={
              beneficiary.verificationResult === 'no-match'
                ? 'The name does not match'
                : beneficiary.verificationResult === 'close-match'
                  ? 'The name is close, but not exact'
                  : 'We could not check the name'
            }
          >
            {beneficiary.verificationResult === 'no-match' ? (
              <>
                The name you have for this account does not match the name the receiving bank holds.
                Payments to the wrong account may not be recoverable.
              </>
            ) : beneficiary.verificationResult === 'close-match' ? (
              <>
                The receiving bank holds a similar but different name. Check with the payee before
                sending.
              </>
            ) : (
              <>
                Account name checking is not available for this destination, so we cannot confirm who
                will receive the money.
              </>
            )}
          </Notice>

          <div className="tm-card">
            <DetailList
              items={[
                { label: 'You are paying', value: beneficiary.name },
                ...(beneficiary.accountNumber
                  ? [{ label: 'Account number', value: beneficiary.accountNumber }]
                  : []),
                ...(beneficiary.sortCode
                  ? [{ label: 'Sort code', value: formatSortCode(beneficiary.sortCode) }]
                  : []),
                ...(beneficiary.iban ? [{ label: 'IBAN', value: formatIban(beneficiary.iban) }] : []),
              ]}
            />
          </div>

          <div className="tm-stack tm-stack--tight">
            <button type="button" className="tm-btn tm-btn--block" onClick={() => setStep('details')}>
              I have checked — continue
            </button>
            <button
              type="button"
              className="tm-btn tm-btn--secondary tm-btn--block"
              onClick={() => setStep('beneficiary')}
            >
              Choose someone else
            </button>
          </div>
        </div>
      ) : null}

      {/* ------------------------------------------------------ details -- */}
      {step === 'details' && sourceAccount && beneficiary ? (
        <form
          className="tm-stack"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void continueFromDetails();
          }}
        >
          <div className="tm-card">
            <p className="tm-eyebrow">Paying</p>
            <strong>{beneficiary.name}</strong>
            <p className="tm-hint" style={{ margin: '2px 0 0' }}>
              from {sourceAccount.displayName} ·{' '}
              <Amount minorUnits={sourceAccount.availableBalance} currency={sourceAccount.currency} />{' '}
              available
            </p>
          </div>

          <div className="tm-field">
            <label className="tm-label" htmlFor="pay-amount">
              Amount ({sourceAccount.currency})
            </label>
            <input
              id="pay-amount"
              ref={amountRef}
              className="tm-input tm-input--amount"
              inputMode="decimal"
              autoComplete="off"
              value={amountText}
              onChange={(e) => setAmountText(e.target.value.replace(/[^\d.,]/g, ''))}
              aria-invalid={fieldErrors['amount'] ? true : undefined}
              aria-describedby={fieldErrors['amount'] ? 'pay-amount-error' : undefined}
            />
            <div className="tm-field__error-slot">
              {fieldErrors['amount'] ? (
                <span className="tm-field__error" id="pay-amount-error">
                  {fieldErrors['amount']}
                </span>
              ) : null}
            </div>
          </div>

          <div className="tm-field">
            <label className="tm-label" htmlFor="pay-reference">
              Reference
            </label>
            <input
              id="pay-reference"
              ref={referenceRef}
              className="tm-input"
              maxLength={18}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              aria-invalid={fieldErrors['reference'] ? true : undefined}
              aria-describedby={fieldErrors['reference'] ? 'pay-ref-error' : 'pay-ref-hint'}
            />
            <div className="tm-field__error-slot">
              {fieldErrors['reference'] ? (
                <span className="tm-field__error" id="pay-ref-error">
                  {fieldErrors['reference']}
                </span>
              ) : (
                <span className="tm-hint" id="pay-ref-hint">
                  Shown on the payee's statement. Up to 18 characters.
                </span>
              )}
            </div>
          </div>

          <div className="tm-field">
            <label className="tm-label" htmlFor="pay-note">
              Note to the payee (optional)
            </label>
            <input
              id="pay-note"
              className="tm-input"
              maxLength={60}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="tm-field__error-slot" />
          </div>

          <button type="submit" className="tm-btn tm-btn--block">
            Continue to review
          </button>
        </form>
      ) : null}

      {/* ------------------------------------------------------- review -- */}
      {step === 'review' && sourceAccount && beneficiary && draft ? (
        <div className="tm-stack">
          <div className="tm-card" style={{ textAlign: 'center' }}>
            <p className="tm-eyebrow">You are sending</p>
            <Amount minorUnits={draft.amount} currency={draft.currency} size="hero" />
            <p className="tm-muted" style={{ margin: '6px 0 0' }}>
              to {beneficiary.name}
            </p>
          </div>

          {submitError ? (
            <Notice tone="danger" title="The payment was not sent">
              {submitError}
            </Notice>
          ) : null}

          {validation?.businessWarnings.map((warning) => (
            <Notice key={warning} tone="warning" title="Check before you send">
              {warning}
            </Notice>
          ))}

          {validation?.approvalRequired ? (
            <Notice tone="info" title="This payment needs approval">
              It is{' '}
              <Amount
                minorUnits={validation.approvalThreshold ?? 0}
                currency={draft.currency}
              />{' '}
              or more, so another authorised person must approve it before the money moves.
            </Notice>
          ) : null}

          <div className="tm-card">
            <DetailList
              items={[
                { label: 'From', value: sourceAccount.displayName },
                { label: 'To', value: beneficiary.name },
                {
                  label: 'Amount',
                  value: <Amount minorUnits={draft.amount} currency={draft.currency} />,
                },
                { label: 'Reference', value: draft.reference },
                ...(draft.noteToBeneficiary
                  ? [{ label: 'Note', value: draft.noteToBeneficiary }]
                  : []),
                {
                  label: 'Payee check',
                  value: <StatusBadge status={beneficiary.verificationResult} />,
                },
                {
                  label: 'Arrives',
                  value: 'Usually within seconds',
                },
              ]}
            />
          </div>

          {(validation?.businessWarnings.length ?? 0) > 0 ? (
            <label className="tm-inline" style={{ alignItems: 'flex-start', gap: 10 }}>
              <input
                type="checkbox"
                checked={acknowledgedWarning}
                onChange={(e) => setAcknowledgedWarning(e.target.checked)}
                style={{ marginBlockStart: 3, width: 20, height: 20 }}
              />
              <span className="tm-muted">
                I have checked these details and want to send this payment.
              </span>
            </label>
          ) : null}

          <button
            type="button"
            className="tm-btn tm-btn--block"
            disabled={warningsBlockingContinue}
            onClick={() => void startSca()}
          >
            Confirm and send
          </button>
          <button
            type="button"
            className="tm-btn tm-btn--secondary tm-btn--block"
            onClick={() => setStep('details')}
          >
            Change details
          </button>
        </div>
      ) : null}

      {/* ---------------------------------------------------------- sca -- */}
      {step === 'sca' ? (
        <div className="tm-stack">
          <p className="tm-muted">
            Enter the 6-digit code from your authenticator app to authorise this payment.
          </p>
          <div className="tm-field">
            <label className="tm-label" htmlFor="pay-sca">
              Authentication code
            </label>
            <input
              id="pay-sca"
              className="tm-input"
              inputMode="numeric"
              maxLength={6}
              value={scaCode}
              onChange={(e) => setScaCode(e.target.value.replace(/\D/g, ''))}
              aria-invalid={scaError ? true : undefined}
              aria-describedby={scaError ? 'pay-sca-error' : 'pay-sca-hint'}
              style={{ letterSpacing: '0.4em' }}
            />
            <div className="tm-field__error-slot">
              {scaError ? (
                <span className="tm-field__error" id="pay-sca-error">
                  {scaError}
                </span>
              ) : (
                <span className="tm-hint" id="pay-sca-hint">
                  Demo code: 481920
                </span>
              )}
            </div>
          </div>
          {/* Never live before the challenge exists, or a fast tap is dropped. */}
          <button
            type="button"
            className="tm-btn tm-btn--block"
            disabled={scaCode.length !== 6 || submitMutation.pending || challengeId === null}
            onClick={() => void confirmAndSend()}
          >
            {submitMutation.pending ? 'Sending…' : 'Authorise payment'}
          </button>
          <button
            type="button"
            className="tm-btn tm-btn--secondary tm-btn--block"
            onClick={() => setStep('review')}
            disabled={submitMutation.pending}
          >
            Back to review
          </button>
        </div>
      ) : null}

      {/* ------------------------------------------------------- result -- */}
      {step === 'result' && result ? (
        <div className="tm-stack">
          {/* A financial outcome gets a full result screen, never a toast alone. */}
          <div className="tm-card" style={{ textAlign: 'center' }} role="status">
            <ResultMedallion
              tone={
                result.outcome === 'completed'
                  ? 'success'
                  : result.outcome === 'awaiting-approval'
                    ? 'warning'
                    : 'danger'
              }
            />
            <p className="tm-eyebrow">
              {result.outcome === 'completed'
                ? 'Payment sent'
                : result.outcome === 'awaiting-approval'
                  ? 'Sent for approval'
                  : 'Payment failed'}
            </p>
            <Amount
              minorUnits={result.payment.amount}
              currency={result.payment.currency}
              size="hero"
            />
            <p className="tm-muted" style={{ margin: '6px 0 12px' }}>
              to {beneficiary?.name ?? 'your payee'}
            </p>
            <StatusBadge status={result.payment.status} />
          </div>

          {result.outcome === 'awaiting-approval' ? (
            <Notice tone="info" title="Waiting for approval">
              No money has moved yet. An authorised approver has been notified and will see this in
              their approvals list.
            </Notice>
          ) : null}

          {result.outcome === 'failed' ? (
            <Notice tone="danger" title="The payment did not go through">
              {result.payment.failureReason ?? 'The receiving bank rejected the payment.'} No money
              left your account.
            </Notice>
          ) : null}

          {/* The receipt: everything the medallion card is too terse to hold —
              who the money moved between, the account it landed on, and when. */}
          <section aria-labelledby="pay-receipt" className="tm-card">
            <SectionHeader title="Receipt" id="pay-receipt" />
            <div style={{ marginBlockStart: 12 }}>
              <DetailList
                items={[
                  { label: 'From', value: sourceAccount?.displayName ?? '—' },
                  {
                    label: 'To',
                    value: beneficiary?.name ?? 'your payee',
                    ...(beneficiary?.iban
                      ? { hint: formatIban(beneficiary.iban) }
                      : beneficiary?.sortCode
                        ? {
                            hint: `${formatSortCode(beneficiary.sortCode)} · ${beneficiary.accountNumber ?? ''}`,
                          }
                        : {}),
                  },
                  {
                    label: 'Amount',
                    value: (
                      <Amount minorUnits={result.payment.amount} currency={result.payment.currency} />
                    ),
                  },
                  { label: 'Reference', value: result.payment.reference },
                  ...(result.payment.noteToBeneficiary
                    ? [{ label: 'Note', value: result.payment.noteToBeneficiary }]
                    : []),
                  { label: 'Date', value: formatDateTime(result.payment.createdAt) },
                  { label: 'Status', value: <StatusBadge status={result.payment.status} /> },
                  { label: 'Payment ID', value: result.payment.id },
                  { label: 'Attempt', value: String(result.attempt.attemptNumber) },
                  ...(result.attempt.providerReference
                    ? [{ label: 'Provider reference', value: result.attempt.providerReference }]
                    : []),
                ]}
              />
            </div>
          </section>

          <div className="tm-stack tm-stack--tight">
            <Link to={`/mobile/payments/${result.payment.id}`} className="tm-btn tm-btn--block">
              View payment
            </Link>
            <Link to="/mobile/home" className="tm-btn tm-btn--secondary tm-btn--block">
              Back to home
            </Link>
          </div>
        </div>
      ) : null}

      {/* Persistent back control for steps beyond the first. */}
      {step !== 'source' && step !== 'result' && step !== 'sca' ? (
        <button
          type="button"
          className="tm-btn tm-btn--quiet"
          style={{ marginBlockStart: 12 }}
          onClick={goBack}
        >
          Back a step
        </button>
      ) : null}

      {/* Screen-reader running total so the amount is confirmable at every step. */}
      <span className="tm-visually-hidden" aria-live="polite">
        {step === 'review' && draft
          ? `Reviewing a payment of ${formatAmount(draft.amount, draft.currency)}`
          : ''}
      </span>
    </>
  );
}
